import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';

/**
 * usePermissions Composable
 *
 * Frontend permission kontrolü için Vue composable.
 * Backend'den gelen yetkileri kullanarak UI'da yetki kontrolü yapar.
 *
 * Kullanım:
 * const { can, canAny, canAll, hasRole, hasAnyRole, isSuperAdmin } = usePermissions();
 *
 * if (can('employees.view.all')) {
 *   // Show employee list
 * }
 */
export function usePermissions() {
    const page = usePage();

    // Get auth data from Inertia shared props
    const auth = computed(() => page.props?.auth || {});
    const user = computed(() => auth.value.user || null);
    const permissions = computed(() => user.value?.permissions || []);
    const roles = computed(() => user.value?.roles || []);

    /**
     * Kullanıcının belirtilen yetkisi var mı?
     *
     * @param {string} permission - Yetki adı (örn: 'employees.view.all')
     * @returns {boolean}
     */
    const can = (permission) => {
        if (!user.value) return false;

        // Super admin her şeyi yapabilir
        if (isSuperAdmin.value) return true;

        // Sadece tenant_admin tüm yetkileri bypass eder
        // NOT: manager, owner, company_authorized artık bypass ETMEZ.
        // Bu roller atanmış permission'lar üzerinden çalışır.
        if (hasAnyRole(['tenant_admin'])) return true;

        // Exact permission check
        if (permissions.value.includes(permission)) {
            return true;
        }

        // Broader scope check
        // Eğer .all varsa, .team, .project, .own da vardır
        const parts = permission.split('.');
        if (parts.length >= 3) {
            const scope = parts[parts.length - 1];
            const basePermission = parts.slice(0, -1).join('.');

            // Scope hierarchy: all > project > team > own
            const scopeHierarchy = ['all', 'project', 'team', 'own'];
            const currentScopeIndex = scopeHierarchy.indexOf(scope);

            if (currentScopeIndex > -1) {
                // Check broader scopes
                for (let i = 0; i < currentScopeIndex; i++) {
                    const broaderPermission = `${basePermission}.${scopeHierarchy[i]}`;
                    if (permissions.value.includes(broaderPermission)) {
                        return true;
                    }
                }
            }
        }

        return false;
    };

    /**
     * Kullanıcının belirtilen yetkilerden en az birisi var mı?
     *
     * @param {string[]} permissionList - Yetki listesi
     * @returns {boolean}
     */
    const canAny = (permissionList) => {
        return permissionList.some(permission => can(permission));
    };

    /**
     * Kullanıcının belirtilen yetkilerin hepsi var mı?
     *
     * @param {string[]} permissionList - Yetki listesi
     * @returns {boolean}
     */
    const canAll = (permissionList) => {
        return permissionList.every(permission => can(permission));
    };

    /**
     * Kullanıcının belirtilen rolü var mı?
     *
     * @param {string} roleName - Rol adı
     * @returns {boolean}
     */
    const hasRole = (roleName) => {
        if (!user.value) return false;
        return roles.value.some(role => role.name === roleName);
    };

    /**
     * Kullanıcının belirtilen rollerden en az birisi var mı?
     *
     * @param {string[]} roleNames - Rol listesi
     * @returns {boolean}
     */
    const hasAnyRole = (roleNames) => {
        if (!user.value) return false;
        return roleNames.some(roleName => hasRole(roleName));
    };

    /**
     * Kullanıcının belirtilen rollerinin hepsi var mı?
     *
     * @param {string[]} roleNames - Rol listesi
     * @returns {boolean}
     */
    const hasAllRoles = (roleNames) => {
        if (!user.value) return false;
        return roleNames.every(roleName => hasRole(roleName));
    };

    /**
     * Super admin mi?
     */
    const isSuperAdmin = computed(() => {
        // Check backend flag first
        if (page.props?.auth?.is_super_admin) return true;
        // Check landlord roles
        const landlordRoles = user.value?.landlord_roles || [];
        if (landlordRoles.some(role => role.name === 'landlord_admin' || role.name === 'admin')) return true;
        return false;
    });

    /**
     * Tenant admin mi?
     */
    const isTenantAdmin = computed(() => {
        return hasRole('tenant_admin');
    });

    /**
     * Admin (super veya tenant admin)?
     */
    const isAdmin = computed(() => {
        return isSuperAdmin.value || isTenantAdmin.value;
    });

    /**
     * Kullanıcı giriş yapmış mı?
     */
    const isAuthenticated = computed(() => {
        return user.value !== null;
    });

    /**
     * Kullanıcının tüm yetkilerini al
     */
    const getAllPermissions = computed(() => {
        return permissions.value;
    });

    /**
     * Kullanıcının tüm rollerini al
     */
    const getAllRoles = computed(() => {
        return roles.value;
    });

    /**
     * Belirli bir modül yetkisi var mı?
     *
     * @param {string} moduleName - Modül adı (örn: 'employees', 'timesheets')
     * @returns {boolean}
     */
    const hasModuleAccess = (moduleName) => {
        if (!user.value) return false;
        if (isSuperAdmin.value) return true;

        // Check if user has any permission for this module
        return permissions.value.some(permission =>
            permission.startsWith(`${moduleName}.`)
        );
    };

    /**
     * Modül için belirli bir action yetkisi var mı?
     *
     * @param {string} moduleName - Modül adı
     * @param {string} action - Action (view, create, edit, delete, approve, etc.)
     * @returns {boolean}
     */
    const canInModule = (moduleName, action) => {
        if (!user.value) return false;
        if (isSuperAdmin.value) return true;

        // Check for any scope (.all, .project, .team, .own)
        return permissions.value.some(permission =>
            permission.startsWith(`${moduleName}.${action}.`)
        );
    };

    return {
        // Permission checks
        can,
        canAny,
        canAll,

        // Role checks
        hasRole,
        hasAnyRole,
        hasAllRoles,

        // Admin checks
        isSuperAdmin,
        isTenantAdmin,
        isAdmin,

        // Authentication
        isAuthenticated,

        // Getters
        getAllPermissions,
        getAllRoles,
        user,
        permissions,
        roles,

        // Module helpers
        hasModuleAccess,
        canInModule,
    };
}
